import {
  // DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Popover,
  Select,
  Switch,
  Upload,
  type FormInstance,
} from "antd";
import ChevronDownIcon from "../../../../../assets/icons/ChevronDownIcon";
// import DatePickerIcon from "../../../../../assets/icons/DatePickerIcon";
import TextArea from "antd/es/input/TextArea";
import { useState } from "react";
import {
  useGetCategoriesQuery,
  useGetSubcategoriesQuery,
} from "../../../../../app/features/admin/admin.api";
import { useUploadFileMutation } from "../../../../../app/features/auth/auth.api";
import { ENV_CONFIG } from "../../../../../config/global.config";
import { errorToast } from "../../../../../utils/error.utils";
import FileUploadIcon from "../../_icons/FileUploadIcon";
import InfoIcon from "../../_icons/InfoIcon";

type Props = {
  form: FormInstance<Record<string, unknown>>;
};

type UploaderType = "Thumbnail" | "Banner";

export default function GeneralCourseInfo({ form }: Props) {
  const [uploaderType, setUploaderType] = useState<UploaderType | undefined>(
    undefined,
  );
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesQuery({});
  const { data: subcategories, isLoading: subcategoriesLoading } =
    useGetSubcategoriesQuery();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  // Auto-generate slug from title
  // const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const title = e.target.value;
  //   const slug = title
  //     .toLowerCase()
  //     .replace(/[^a-z0-9]+/g, "-")
  //     .replace(/^-|-$/g, "");
  //   form.setFieldValue("slug", slug);
  // };

  const handleFileUpload = async (file: File, type: "Thumbnail" | "Banner") => {
    setUploaderType(type);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      setUploaderType(undefined);
      return response?.file?.url;
    } catch (error) {
      setUploaderType(undefined);
      console.error("Upload failed:", error);
      errorToast(error, "File upload failed");
      throw error;
    }
  };

  const thumbnailUrl = Form.useWatch("thumbnailUrl", form) as
    | string
    | undefined;
  const bannerUrl = Form.useWatch("bannerUrl", form) as string | undefined;

  return (
    <div className="flex flex-col justify-start gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        <div>
          <Flex justify="space-between" align="center" gap={4}>
            <div className="justify-start text-slate-700 text-sm font-medium">
              Course Thumbnail*
            </div>

            <Popover
              content={thumbnailContent}
              placement="topRight"
              trigger="hover"
            >
              <div className="inline-block cursor-pointer">
                <InfoIcon className="text-[#455468] text-lg" />
              </div>
            </Popover>
          </Flex>
          <Form.Item
            name="thumbnailUrl"
            rules={[
              { required: true, message: "Please upload course thumbnail" },
            ]}
            className="mb-0 mt-1"
            getValueFromEvent={(e: any) => {
              if (typeof e === "string") return e;
              const url = e?.file?.response;
              if (url && typeof url === "string") {
                return `${ENV_CONFIG?.IMAGE_URL}/${url}`;
              }
              return form.getFieldValue("thumbnailUrl");
            }}
          >
            <Upload.Dragger
              customRequest={({ file, onSuccess, onError }) => {
                handleFileUpload(file as File, "Thumbnail")
                  .then((url) => onSuccess?.(url))
                  .catch((e) => onError?.(e));
              }}
              showUploadList={false}
              accept=".jpg,.png,.jpeg"
              className="w-full !bg-stone-50 !rounded-lg !border-dashed !border-[#94A3B8]"
            >
              <div className="flex flex-col items-center justify-center py-6 h-64 relative">
                {isUploading && uploaderType === "Thumbnail" && (
                  <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  </div>
                )}
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt="Thumbnail"
                    className="max-h-52 object-contain"
                  />
                ) : (
                  <>
                    <div className="w-20 h-16 relative mb-4">
                      <FileUploadIcon />
                    </div>
                    <div className="text-zinc-700 text-sm font-medium">
                      Drop your thumbnail here, or browse
                    </div>
                    <div className="text-slate-500 text-xs font-normal mt-1">
                      Recommended: .jpg, .png, .jpeg format, max 10MB
                    </div>
                    <div className="px-3 py-2.5 bg-white rounded-md outline outline-1 outline-zinc-200 text-slate-600 text-sm font-medium mt-3.5">
                      Upload Thumbnail
                    </div>
                  </>
                )}
              </div>
            </Upload.Dragger>
          </Form.Item>
        </div>

        <div>
          <Flex justify="space-between" align="center" gap={4}>
            <div className="justify-start text-slate-700 text-sm font-medium">
              Course Banner*
            </div>
            <Popover
              content={bannerContent}
              placement="topRight"
              trigger="hover"
            >
              <div className="inline-block cursor-pointer">
                <InfoIcon className="text-[#455468] text-lg" />
              </div>
            </Popover>
          </Flex>
          <Form.Item
            name="bannerUrl"
            rules={[{ required: true, message: "Please upload course banner" }]}
            className="mb-0 mt-1"
            getValueFromEvent={(e: any) => {
              if (typeof e === "string") return e;
              const url = e?.file?.response;
              if (url && typeof url === "string") {
                return `${ENV_CONFIG?.IMAGE_URL}/${url}`;
              }
              return form.getFieldValue("bannerUrl");
            }}
          >
            <Upload.Dragger
              customRequest={({ file, onSuccess, onError }) => {
                handleFileUpload(file as File, "Banner")
                  .then((url) => onSuccess?.(url))
                  .catch((e) => onError?.(e));
              }}
              showUploadList={false}
              accept=".jpg,.png,.jpeg"
              className="w-full !bg-stone-50 !rounded-lg !border-dashed !border-[#94A3B8]"
            >
              <div className="flex flex-col items-center justify-center py-6 h-64 relative">
                {isUploading && uploaderType === "Banner" && (
                  <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  </div>
                )}
                {bannerUrl ? (
                  <img
                    src={bannerUrl}
                    alt="Banner"
                    className="max-h-52 object-contain"
                  />
                ) : (
                  <>
                    <div className="w-20 h-16 relative mb-4">
                      <FileUploadIcon />
                    </div>
                    <div className="text-zinc-700 text-sm font-medium">
                      Drop your banner here, or browse
                    </div>
                    <div className="text-slate-500 text-xs font-normal mt-1">
                      Recommended: .jpg, .png, .jpeg format, max 10MB
                    </div>
                    <div className="px-3 py-2.5 bg-white rounded-md outline outline-1 outline-zinc-200 text-slate-600 text-sm font-medium mt-3.5">
                      Upload Banner
                    </div>
                  </>
                )}
              </div>
            </Upload.Dragger>
          </Form.Item>
        </div>
      </div>

      <Form.Item
        name="title"
        label="Course Title"
        rules={[{ required: true, message: "Please enter course title" }]}
      >
        <Input
          placeholder="Course Title"
          size="large"
          // onChange={handleTitleChange}
        />
      </Form.Item>

      {/* <Form.Item
        name="slug"
        label="Course Slug"
        rules={[{ required: true, message: "Please enter course slug" }]}
      >
        <Input placeholder="course-slug" size="large" />
      </Form.Item> */}

      <Form.Item
        name="shortDescription"
        label="Short Description"
        rules={[
          {
            required: true,
            message: "Please enter a short description",
          },
        ]}
      >
        <TextArea
          rows={4}
          placeholder="Brief summary of the course (max 200 characters)"
          maxLength={200}
          size="large"
        />
      </Form.Item>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Form.Item
          label="Primary Category"
          name="categoryId"
          rules={[
            {
              required: true,
              message: "Please select primary category",
            },
          ]}
        >
          <Select
            placeholder="Select the main category"
            suffixIcon={<ChevronDownIcon className="text-[#8897AE]" />}
            allowClear
            size="large"
            loading={categoriesLoading}
            options={
              Array.isArray(categories)
                ? categories.map((cat) => ({
                    label: cat.name,
                    value: cat.id,
                  }))
                : []
            }
          />
        </Form.Item>

        <Form.Item
          label="Sub-category"
          name="subCategoryId"
          rules={[
            {
              required: false,
              message: "Please select sub-category",
            },
          ]}
        >
          <Select
            placeholder="Select a sub-category"
            suffixIcon={<ChevronDownIcon className="text-[#8897AE]" />}
            allowClear
            size="large"
            loading={subcategoriesLoading}
            options={
              Array.isArray(subcategories)
                ? subcategories.map((sub) => ({
                    label: sub.name,
                    value: sub.id,
                  }))
                : []
            }
          />
        </Form.Item>

        <Form.Item
          label="Language"
          name="language"
          rules={[
            {
              required: true,
              message: "Please select language",
            },
          ]}
        >
          <Select
            placeholder="Select a language"
            suffixIcon={<ChevronDownIcon className="text-[#8897AE]" />}
            allowClear
            size="large"
            options={[
              { label: "Bangla", value: "Bangla" },
              { label: "English", value: "English" },
              { label: "Hindi", value: "Hindi" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Level"
          name="level"
          rules={[
            {
              required: true,
              message: "Please select level",
            },
          ]}
        >
          <Select
            placeholder="Select a level"
            suffixIcon={<ChevronDownIcon className="text-[#8897AE]" />}
            allowClear
            size="large"
            options={[
              { label: "Beginner", value: "Beginner" },
              { label: "Intermediate", value: "Intermediate" },
              { label: "Advanced", value: "Advanced" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Course Price"
          name="price"
          rules={[
            { required: true, message: "Please enter course price" },
            {
              type: "number",
              min: 0,
              message: "Price cannot be negative",
            },
          ]}
        >
          <InputNumber
            placeholder="Enter Price"
            size="large"
            // min={0}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          label="Offer Price"
          name="offerPrice"
          rules={[
            { required: false, message: "Please enter offer price" },
            {
              type: "number",
              min: 0,
              message: "Offer price cannot be negative",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const price = getFieldValue("price");
                if (!value || !price || value <= price) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Offer price cannot be greater than course price"),
                );
              },
            }),
          ]}
        >
          <InputNumber
            placeholder="Enter Offer Price"
            size="large"
            // min={0}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          label="Duration Label"
          name="durationLabel"
          rules={[{ required: true, message: "Please enter duration label" }]}
        >
          <Input placeholder="e.g., 4 weeks, 20 hours" size="large" />
        </Form.Item>

        {/* Commented out: Not in API */}
        {/* <Form.Item
          label="Start Date"
          name="startDate"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <DatePicker
            picker="date"
            format="YYYY/MM/DD"
            placeholder="Select special price start date"
            style={{ width: "100%" }}
            size="large"
            suffixIcon={<DatePickerIcon className="text-[#8897AE]" />}
            allowClear
          />
        </Form.Item> */}

        {/* <Form.Item
          label="End Date"
          name="endDate"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <DatePicker
            picker="date"
            format="YYYY/MM/DD"
            placeholder="Select special price end date"
            style={{ width: "100%" }}
            size="large"
            suffixIcon={<DatePickerIcon className="text-[#8897AE]" />}
            allowClear
          />
        </Form.Item> */}
      </div>

      <Form.Item
        label="Description"
        name="description"
        rules={[
          {
            required: false,
            message: "Please enter course description",
          },
        ]}
      >
        <TextArea
          rows={5}
          placeholder="Explain what learners will gain, who the course is for, and what's included..."
          size="large"
        />
      </Form.Item>

      {/* Commented out: These will be handled via Course Highlights API */}
      {/* <Form.Item
        label="What Learners Will Learn"
        name="whatLearnersWillLearn"
        rules={[
          {
            required: false,
            message: "Please enter what learners will learn",
          },
        ]}
      >
        <TextArea rows={4} placeholder="Write here..." size="large" />
      </Form.Item> */}

      {/* <Form.Item
        label="Who This Course Is For"
        name="whoThisCourseIsFor"
        rules={[
          {
            required: false,
            message: "Please enter who this course is for",
          },
        ]}
      >
        <TextArea rows={4} placeholder="Write here..." size="large" />
      </Form.Item> */}

      {/* <Form.Item
        label="This Course Includes"
        name="thisCourseIncludes"
        rules={[
          {
            required: false,
            message: "Please enter what this course includes",
          },
        ]}
      >
        <TextArea rows={4} placeholder="Write here..." size="large" />
      </Form.Item> */}

      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Form.Item
          label="Visibility"
          name="visibility"
          rules={[{ required: true, message: "Please select visibility" }]}
        >
          <Select
            placeholder="Select visibility"
            suffixIcon={<ChevronDownIcon className="text-[#8897AE]" />}
            size="large"
            options={[
              { label: "Public", value: "PUBLIC" },
              { label: "Organization Only", value: "ORG_ONLY" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Organization UUID"
          name="organizationUuid"
          rules={[{ required: false }]}
        >
          <Input
            placeholder="Enter organization UUID (if ORG_ONLY)"
            size="large"
          />
        </Form.Item>
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Form.Item
          label="Certificate Enabled"
          name="isCertificateEnabled"
          valuePropName="checked"
        >
          <Switch checkedChildren="Yes" unCheckedChildren="No" />
        </Form.Item>

        <Form.Item
          label="Featured Course"
          name="isFeatured"
          valuePropName="checked"
        >
          <Switch checkedChildren="Yes" unCheckedChildren="No" />
        </Form.Item>
      </div>

      {/* Commented out: Tag is not in CreateCourseDto API */}
      {/* <Form.Item label="Tag (Max 10)" name="tags" rules={[{ required: false }]}>
        <Select
          mode="tags"
          size="large"
          placeholder="Enter tags"
          style={{ width: "100%" }}
          suffixIcon={<ChevronDownIcon className="text-[#8897AE]" />}
        />
      </Form.Item> */}
    </div>
  );
}

const thumbnailContent = (
  <div className="text-xs whitespace-pre-line">
    <Flex align="center" justify="start" gap={4}>
      <InfoIcon className="text-lg text-[#155DFC]" />
      <div className="justify-start text-blue-900 text-base font-normal">
        Thumbnail Guidelines:
      </div>
    </Flex>
    <div className="ml-5">
      <div className="justify-start text-blue-800 text-sm font-normal mt-1">
        • 16:9 aspect ratio (1280x720 recommended)
      </div>
      <div className="justify-start text-blue-800 text-sm font-normal">
        • High-quality image (JPG or PNG)
      </div>
      <div className="justify-start text-blue-800 text-sm font-normal">
        • Include text overlay describing your course
      </div>
      <div className="justify-start text-blue-800 text-sm font-normal">
        • Use contrasting colors for readability
      </div>
    </div>
  </div>
);

const bannerContent = (
  <div className="text-xs whitespace-pre-line">
    <Flex align="center" justify="start" gap={4}>
      <InfoIcon className="text-lg text-[#155DFC]" />
      <div className="justify-start text-blue-900 text-base font-normal">
        Banner Guidelines:
      </div>
    </Flex>
    <div className="ml-5">
      <div className="justify-start text-blue-800 text-sm font-normal mt-1">
        • 16:9 aspect ratio (1920x1080 recommended)
      </div>
      <div className="justify-start text-blue-800 text-sm font-normal">
        • High-quality image (JPG or PNG)
      </div>
      <div className="justify-start text-blue-800 text-sm font-normal">
        • Represents the course theme
      </div>
      <div className="justify-start text-blue-800 text-sm font-normal">
        • Use professional and clear imagery
      </div>
    </div>
  </div>
);
